import { useMemo, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { X, UploadSimple, MicrosoftExcelLogo, CheckCircle, ArrowRight, ArrowLeft, Warning, Users, Wrench, Receipt } from "@phosphor-icons/react";
import * as XLSX from "xlsx";

/**
 * Multi-sheet import wizard for migrating from another POS (RepairDesk focus).
 * - Upload one xlsx → auto-detect sheets by name → preview each → import in order.
 * - Customers first, then Tickets, then Invoices (so FK-by-phone-or-email links resolve).
 * - Auto-maps RepairDesk column headers (documented at help.repairdesk.co).
 */
const PRESETS = {
  customers: {
    label: "Customers",
    icon: Users,
    sheetAliases: ["customers", "customer", "contacts"],
    endpoint: "/customers/import",
    payloadKey: "customers",
    fields: [
      { k: "name", label: "Name", required: true, aliases: ["name", "full name", "customer name", "customer"] },
      { k: "first_name", label: "First name", aliases: ["first name", "firstname", "given name"] },
      { k: "last_name", label: "Last name", aliases: ["last name", "lastname", "surname", "family name"] },
      { k: "phone", label: "Phone", aliases: ["phone", "mobile", "mobile number", "phone number", "contact"] },
      { k: "email", label: "Email", aliases: ["email", "email address", "e-mail"] },
      { k: "notes", label: "Notes", aliases: ["notes", "comments", "description"] },
    ],
    rowBuilder: (m, r) => {
      const first = r[m.first_name] || "";
      const last = r[m.last_name] || "";
      const name = (r[m.name] || `${first} ${last}`).trim();
      return name ? {
        name,
        phone: (r[m.phone] || "").trim(),
        email: (r[m.email] || "").trim(),
        notes: (r[m.notes] || "").trim(),
      } : null;
    },
  },
  tickets: {
    label: "Tickets",
    icon: Wrench,
    sheetAliases: ["tickets", "ticket", "repairs", "repair tickets", "jobs"],
    endpoint: "/repair-tickets/import",
    payloadKey: "tickets",
    fields: [
      { k: "ticket_number", label: "Ticket #", aliases: ["ticket no", "ticket number", "ticket #", "id", "ticket id"] },
      { k: "customer_name", label: "Customer", aliases: ["customer", "customer name", "name"] },
      { k: "customer_phone", label: "Phone", aliases: ["phone", "mobile", "contact"] },
      { k: "customer_email", label: "Email", aliases: ["email", "customer email"] },
      { k: "device_make", label: "Device make", aliases: ["manufacturer", "make", "brand", "device make"] },
      { k: "device_model", label: "Device model", aliases: ["model", "device", "device model"] },
      { k: "imei", label: "IMEI / Serial", aliases: ["imei", "serial", "imei/serial", "imei or serial", "serial number"] },
      { k: "issue", label: "Issue", aliases: ["issue", "problem", "description", "fault", "notes"] },
      { k: "estimated_cost", label: "Estimated cost", aliases: ["estimate", "estimated", "estimated amount", "estimated cost", "cost"] },
      { k: "final_cost", label: "Final cost", aliases: ["total", "final", "final amount", "amount"] },
      { k: "status", label: "Status", aliases: ["status", "state"] },
      { k: "created_at", label: "Created", aliases: ["created", "created at", "date", "date created"] },
    ],
    rowBuilder: (m, r) => {
      if (!(r[m.customer_name] || r[m.customer_phone] || r[m.customer_email])) return null;
      return {
        ticket_number: (r[m.ticket_number] || "").trim(),
        customer_name: (r[m.customer_name] || "").trim(),
        customer_phone: (r[m.customer_phone] || "").trim(),
        customer_email: (r[m.customer_email] || "").trim(),
        device_make: (r[m.device_make] || "").trim(),
        device_model: (r[m.device_model] || "").trim(),
        imei: (r[m.imei] || "").trim(),
        issue: (r[m.issue] || "").trim(),
        estimated_cost: parseFloat(r[m.estimated_cost]) || 0,
        final_cost: parseFloat(r[m.final_cost]) || 0,
        status: (r[m.status] || "picked_up").trim().toLowerCase().replace(/ /g, "_").replace(/-/g, "_"),
        created_at: (r[m.created_at] || "").trim(),
      };
    },
  },
  invoices: {
    label: "Invoices",
    icon: Receipt,
    sheetAliases: ["invoices", "invoice", "sales", "transactions"],
    endpoint: "/sales/import",
    payloadKey: "sales",
    fields: [
      { k: "sale_number", label: "Invoice #", aliases: ["invoice no", "invoice number", "invoice #", "invoice id", "id"] },
      { k: "customer_name", label: "Customer (full)", aliases: ["customer", "customer name"] },
      { k: "customer_first_name", label: "Customer first name", aliases: ["customer first name", "first name"] },
      { k: "customer_last_name", label: "Customer last name", aliases: ["customer last name", "last name"] },
      { k: "customer_phone", label: "Phone", aliases: ["customer phone", "phone"] },
      { k: "customer_mobile", label: "Mobile", aliases: ["customer mobile", "mobile"] },
      { k: "customer_email", label: "Email", aliases: ["email", "customer email"] },
      { k: "customer_address", label: "Address", aliases: ["customer address", "address"] },
      { k: "customer_city", label: "City", aliases: ["customer city", "city"] },
      { k: "customer_state", label: "State", aliases: ["customer state", "state"] },
      { k: "customer_postcode", label: "Postcode", aliases: ["customer postcode", "postcode", "postal code", "zip"] },
      { k: "customer_country", label: "Country", aliases: ["customer country", "country"] },
      { k: "store_name", label: "Store", aliases: ["store name", "store"] },
      { k: "ticket_number", label: "Ticket #", aliases: ["ticket number", "ticket #", "ticket"] },
      { k: "item_code", label: "Item code", aliases: ["item code"] },
      { k: "item_sku", label: "Item SKU", aliases: ["item sku", "sku"] },
      { k: "item_name", label: "Item name", aliases: ["item name", "item"] },
      { k: "description", label: "Description", aliases: ["description", "notes", "items"] },
      { k: "imei_serial", label: "IMEI / Serial", aliases: ["imei/serial", "imei", "serial"] },
      { k: "unit_price", label: "Unit price", aliases: ["price", "unit price"] },
      { k: "quantity", label: "Quantity", aliases: ["quantity", "qty"] },
      { k: "line_tax", label: "Line tax", aliases: ["line tax"] },
      { k: "subtotal", label: "Subtotal", aliases: ["subtotal", "sub total", "sub-total"] },
      { k: "discount", label: "Discount", aliases: ["discount"] },
      { k: "tax", label: "Tax", aliases: ["tax", "gst", "vat"] },
      { k: "total", label: "Total", required: false, aliases: ["total", "amount", "grand total"] },
      { k: "amount_paid", label: "Paid", aliases: ["amount paid", "paid", "tendered", "amount tendered"] },
      { k: "due_amount", label: "Due", aliases: ["due amount", "due", "balance due", "balance"] },
      { k: "payment_method", label: "Payment method", aliases: ["payment method", "method", "payment", "pay method"] },
      { k: "created_at", label: "Invoice date", aliases: ["invoice date", "date", "created", "created at"] },
      { k: "paid_at", label: "Paid date", aliases: ["invoice paid date", "paid date", "paid at"] },
    ],
    rowBuilder: (m, r) => {
      // Build the customer name from whatever's available — full name wins,
      // otherwise stitch first + last.
      const pick = (k) => (m[k] !== undefined && m[k] !== -1 ? (r[m[k]] || "").toString().trim() : "");
      // Dates: XLSX reads with cellDates=true → returns Date objects. toISOString()
      // preserves the historical date and is reliably parsed by the backend.
      const pickDate = (k) => {
        if (m[k] === undefined || m[k] === -1) return "";
        const v = r[m[k]];
        if (v instanceof Date && !isNaN(v.getTime())) return v.toISOString();
        return (v || "").toString().trim();
      };
      const fullName = pick("customer_name");
      const firstName = pick("customer_first_name");
      const lastName = pick("customer_last_name");
      const name = fullName || [firstName, lastName].filter(Boolean).join(" ").trim();
      // Mobile (RepairDesk) usually holds the actual contact number — prefer it over Phone.
      const mobile = pick("customer_mobile");
      const phone = pick("customer_phone");
      const pricePerUnit = parseFloat(pick("unit_price")) || 0;
      const qty = parseFloat(pick("quantity")) || 1;
      const inferredTotal = parseFloat(pick("total")) || (pricePerUnit * qty) || 0;
      // Accept rows with ANY useful data — even 0-total line items (server
      // aggregates them under the same invoice number).
      if (!pick("sale_number") && !inferredTotal && !name) return null;
      return {
        sale_number: pick("sale_number"),
        customer_name: name,
        customer_phone: mobile || phone,
        customer_email: pick("customer_email").toLowerCase(),
        customer_address: pick("customer_address"),
        customer_city: pick("customer_city"),
        customer_state: pick("customer_state"),
        customer_postcode: pick("customer_postcode"),
        customer_country: pick("customer_country"),
        store_name: pick("store_name"),
        ticket_number: pick("ticket_number"),
        item_code: pick("item_code"),
        item_sku: pick("item_sku"),
        item_name: pick("item_name") || pick("description"),
        description: pick("description"),
        imei_serial: pick("imei_serial"),
        unit_price: pricePerUnit,
        quantity: qty,
        line_tax: parseFloat(pick("line_tax")) || 0,
        subtotal: parseFloat(pick("subtotal")) || 0,
        discount: parseFloat(pick("discount")) || 0,
        tax: parseFloat(pick("tax")) || 0,
        total: inferredTotal,
        amount_paid: parseFloat(pick("amount_paid")) || 0,
        due_amount: parseFloat(pick("due_amount")) || 0,
        payment_method: (pick("payment_method") || "cash").toLowerCase(),
        created_at: pickDate("created_at"),
        paid_at: pickDate("paid_at"),
      };
    },
  },
};

function autoMap(headers, fields) {
  const lower = headers.map((h) => (h || "").toString().trim().toLowerCase());
  const out = {};
  fields.forEach((f) => {
    const idx = lower.findIndex((h) => f.aliases.some((a) => h === a));
    if (idx !== -1) { out[f.k] = idx; return; }
    const idx2 = lower.findIndex((h) => f.aliases.some((a) => h.includes(a)));
    if (idx2 !== -1) out[f.k] = idx2;
    else out[f.k] = -1;
  });
  return out;
}

function detectSheetKind(sheetName) {
  const lc = sheetName.toLowerCase().trim();
  for (const [kind, preset] of Object.entries(PRESETS)) {
    if (preset.sheetAliases.some((a) => lc === a || lc.includes(a))) return kind;
  }
  return null;
}

export default function MigrateFromPOSModal({ open, onClose, onCompleted }) {
  const [step, setStep] = useState(0); // 0 upload, 1 preview customers, 2 preview tickets, 3 preview invoices, 4 done
  const [fileName, setFileName] = useState("");
  const [sheets, setSheets] = useState({ customers: null, tickets: null, invoices: null });
  const [mappings, setMappings] = useState({ customers: {}, tickets: {}, invoices: {} });
  const [results, setResults] = useState({});
  const [submitting, setSubmitting] = useState(false);
  // Live progress for the current chunked import step (null when idle)
  const [progress, setProgress] = useState(null);

  if (!open) return null;

  const reset = () => {
    setStep(0); setFileName(""); setSheets({ customers: null, tickets: null, invoices: null });
    setMappings({ customers: {}, tickets: {}, invoices: {} }); setResults({});
  };

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFileName(f.name);
    try {
      const buf = await f.arrayBuffer();
      const wb = XLSX.read(buf, { type: "array", cellDates: true });
      const next = { customers: null, tickets: null, invoices: null };
      const nextMap = { customers: {}, tickets: {}, invoices: {} };
      for (const name of wb.SheetNames) {
        const kind = detectSheetKind(name);
        if (!kind || next[kind]) continue;
        const aoa = XLSX.utils.sheet_to_json(wb.Sheets[name], { header: 1, defval: "", blankrows: false })
          .map((r) => r.map((v) => v === null || v === undefined ? "" : String(v).trim()))
          .filter((r) => r.some((c) => c !== ""));
        if (!aoa.length) continue;
        const headers = aoa[0];
        const rows = aoa.slice(1);
        next[kind] = { sheetName: name, headers, rows };
        nextMap[kind] = autoMap(headers, PRESETS[kind].fields);
      }
      const found = Object.entries(next).filter(([_, v]) => v).map(([k]) => k);
      if (!found.length) {
        toast.error("Couldn't detect any Customers/Tickets/Invoices sheets in this file.");
        return;
      }
      setSheets(next);
      setMappings(nextMap);
      toast.success(`Detected: ${found.join(", ")}`);
      setStep(1);
    } catch (err) {
      toast.error(`Could not read file: ${err.message || err}`);
    } finally { if (e.target) e.target.value = ""; }
  };

  const orderedKinds = ["customers", "tickets", "invoices"];

  const currentKind = orderedKinds.find((k, i) => i === step - 1);

  const runImport = async (kind, onProgress) => {
    const sheet = sheets[kind];
    if (!sheet) return { skipped: true };
    const preset = PRESETS[kind];
    const map = mappings[kind];
    const payloadRows = [];
    for (const r of sheet.rows) {
      const built = preset.rowBuilder(map, r);
      if (built) payloadRows.push(built);
    }
    if (!payloadRows.length) return { imported: 0, skipped: 0, errors: 0 };

    // Chunk large imports — 19k-row XLSX files easily blow past server
    // timeouts and proxy limits. 500 rows/chunk is the sweet spot: big
    // enough to amortise round-trip overhead, small enough to stay under
    // every intermediate timeout (reverse proxy, load balancer, Node req).
    const CHUNK = 500;
    const chunks = [];
    for (let i = 0; i < payloadRows.length; i += CHUNK) {
      chunks.push(payloadRows.slice(i, i + CHUNK));
    }
    onProgress?.({ total: payloadRows.length, done: 0, chunks: chunks.length });

    const agg = { imported: 0, skipped_duplicate: 0, skipped_invalid: 0, errors: [], batch_ids: [] };
    let doneRows = 0;
    for (let i = 0; i < chunks.length; i++) {
      const body = { [preset.payloadKey]: chunks[i] };
      let attempt = 0;
      let lastErr = null;
      while (attempt < 3) {
        try {
          const { data } = await api.post(preset.endpoint, body, { timeout: 10 * 60 * 1000 });
          agg.imported          += Number(data?.imported || 0);
          agg.skipped_duplicate += Number(data?.skipped_duplicate || 0);
          agg.skipped_invalid   += Number(data?.skipped_invalid || 0);
          if (data?.batch_id) agg.batch_ids.push(data.batch_id);
          if (Array.isArray(data?.errors)) agg.errors.push(...data.errors);
          lastErr = null;
          break;
        } catch (e) {
          lastErr = e;
          attempt++;
          if (attempt < 3) {
            await new Promise((r) => setTimeout(r, 1500 * attempt));
          }
        }
      }
      if (lastErr) {
        // Surface the first hard failure — UI will show partial progress.
        throw Object.assign(lastErr, {
          partial: { ...agg, chunk_failed_at: i + 1, chunks_total: chunks.length },
        });
      }
      doneRows += chunks[i].length;
      onProgress?.({ total: payloadRows.length, done: doneRows, chunks: chunks.length, chunkIndex: i + 1 });
    }
    return agg;
  };

  const advance = async () => {
    if (!currentKind) { setStep(step + 1); return; }
    setSubmitting(true);
    setProgress({ total: 0, done: 0, chunks: 0, chunkIndex: 0 });
    try {
      const res = await runImport(currentKind, (p) => setProgress(p));
      setResults((r) => ({ ...r, [currentKind]: res }));
      toast.success(`${PRESETS[currentKind].label}: ${res.imported} imported`);
      setStep(step + 1);
    } catch (e) {
      const partial = e?.partial;
      if (partial) {
        setResults((r) => ({ ...r, [currentKind]: { ...partial, _partial: true } }));
        toast.error(
          `Import stopped at chunk ${partial.chunk_failed_at}/${partial.chunks_total} — ${partial.imported} rows saved. Retry from here.`
        );
      } else {
        toast.error(formatApiError(e));
      }
    } finally {
      setSubmitting(false);
      setProgress(null);
    }
  };

  const skipThis = () => setStep(step + 1);

  const sheetCount = Object.values(sheets).filter(Boolean).length;
  const lastStep = step > orderedKinds.length;

  return (
    <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white w-full max-w-4xl max-h-[90vh] rounded-3xl overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()} style={{ boxShadow: "0 24px 64px rgba(30, 95, 235, 0.25)" }}>
        {/* Header */}
        <div className="px-8 py-6 border-b border-[#F3F4F6] flex items-start justify-between">
          <div>
            <div className="text-[11px] tracking-[0.14em] uppercase text-[#9CA3AF] font-medium">Migrate from another POS</div>
            <h2 className="font-display font-semibold text-2xl tracking-tight text-[#121216] mt-1">Import customers, tickets & invoices</h2>
            <div className="text-[12px] text-[#4B5563] mt-1">One upload · 3 sheets · clean cross-linking by phone & email.</div>
          </div>
          <button onClick={onClose} className="p-2 rounded-full hover:bg-[#F8FAFC] text-[#4B5563]"><X size={16} weight="bold" /></button>
        </div>

        {/* Stepper */}
        <div className="px-8 py-3 bg-[#F8FAFC] border-b border-[#F3F4F6] flex items-center gap-2 text-[11px] font-semibold text-[#4B5563]">
          <StepDot active={step === 0} done={step > 0} label="Upload" />
          <ArrowRight size={10} className="text-[#9CA3AF]" />
          <StepDot active={step === 1} done={step > 1} label="Customers" />
          <ArrowRight size={10} className="text-[#9CA3AF]" />
          <StepDot active={step === 2} done={step > 2} label="Tickets" />
          <ArrowRight size={10} className="text-[#9CA3AF]" />
          <StepDot active={step === 3} done={step > 3} label="Invoices" />
          <ArrowRight size={10} className="text-[#9CA3AF]" />
          <StepDot active={lastStep} done={lastStep} label="Done" />
        </div>

        {/* Body */}
        <div className="flex-1 overflow-auto">
          {step === 0 && (
            <div className="p-10 text-center">
              <label className="inline-block border-2 border-dashed border-[#E5E7EB] hover:border-[#121216] p-10 rounded-2xl cursor-pointer transition-colors max-w-sm">
                <div className="flex items-center justify-center gap-3 mb-3">
                  <MicrosoftExcelLogo size={36} weight="duotone" className="text-[#2F8F6E]" />
                  <UploadSimple size={24} weight="bold" className="text-[#9CA3AF]" />
                </div>
                <div className="font-semibold text-[15px] text-[#121216]">Upload your RepairDesk export</div>
                <div className="text-[12px] text-[#4B5563] mt-1">.xlsx — sheets named Customers / Tickets / Invoices auto-detect</div>
                <input type="file" accept=".xlsx,.xls" className="hidden" onChange={onFile} data-testid="migrate-file-input" />
              </label>
              <div className="mt-8 text-[12px] text-[#4B5563] max-w-md mx-auto leading-relaxed">
                In RepairDesk: <span className="font-semibold text-[#121216]">Reports → Export → All Data (XLSX)</span>. That download typically has the 3 sheets we need.
              </div>
            </div>
          )}

          {step >= 1 && step <= 3 && (() => {
            const kind = orderedKinds[step - 1];
            const sheet = sheets[kind];
            const preset = PRESETS[kind];
            const Icon = preset.icon;
            if (!sheet) {
              return (
                <div className="p-10 text-center" data-testid={`step-${kind}-missing`}>
                  <div className="w-12 h-12 rounded-full bg-[#F8FAFC] mx-auto flex items-center justify-center mb-3">
                    <Warning size={22} weight="duotone" className="text-[#C98B24]" />
                  </div>
                  <div className="font-semibold text-[#121216]">No {preset.label} sheet detected.</div>
                  <div className="text-[12px] text-[#4B5563] mt-1">Skip this step, or go back and upload a file that has a "{preset.label}" sheet.</div>
                </div>
              );
            }
            const map = mappings[kind];
            const preview = sheet.rows.slice(0, 5).map((r) => {
              const obj = {};
              preset.fields.forEach((f) => { obj[f.label] = map[f.k] >= 0 ? (r[map[f.k]] || "") : ""; });
              return obj;
            });
            const mappedCount = preset.fields.filter((f) => map[f.k] >= 0).length;
            return (
              <div className="p-6" data-testid={`step-${kind}-preview`}>
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-full bg-[#DBEAFE] flex items-center justify-center">
                    <Icon size={20} weight="duotone" className="text-[#1E5FEB]" />
                  </div>
                  <div>
                    <div className="font-semibold text-[15px] text-[#121216]">{preset.label} — {sheet.rows.length} rows in "{sheet.sheetName}"</div>
                    <div className="text-[11px] text-[#4B5563]">{mappedCount} / {preset.fields.length} fields auto-mapped</div>
                  </div>
                </div>

                {/* Mapping grid */}
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-5">
                  {preset.fields.map((f) => (
                    <label key={f.k} className="block">
                      <div className="text-[10px] uppercase tracking-[0.14em] text-[#9CA3AF] font-semibold mb-1">{f.label}{f.required && " *"}</div>
                      <select
                        value={map[f.k] ?? -1}
                        onChange={(e) => setMappings((m) => ({ ...m, [kind]: { ...m[kind], [f.k]: Number(e.target.value) } }))}
                        data-testid={`mig-${kind}-map-${f.k}`}
                        className="w-full h-9 px-2 border border-[#E5E7EB] focus:border-[#1E5FEB] focus:outline-none text-[12px] bg-white rounded-lg"
                      >
                        <option value={-1}>—</option>
                        {sheet.headers.map((h, i) => <option key={i} value={i}>{h}</option>)}
                      </select>
                    </label>
                  ))}
                </div>

                {/* Preview table */}
                <div className="border border-[#E5E7EB] rounded-xl overflow-hidden">
                  <div className="text-[10px] uppercase tracking-[0.14em] text-[#9CA3AF] font-semibold px-3 py-2 bg-[#F8FAFC]">Preview (first 5)</div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-[11px]">
                      <thead className="bg-[#F8FAFC] border-t border-[#F3F4F6]">
                        <tr>{preset.fields.slice(0, 6).map((f) => <th key={f.k} className="text-left px-3 py-2 font-semibold text-[#4B5563]">{f.label}</th>)}</tr>
                      </thead>
                      <tbody>
                        {preview.map((r, i) => (
                          <tr key={i} className="border-t border-[#F3F4F6]">
                            {preset.fields.slice(0, 6).map((f) => <td key={f.k} className="px-3 py-2 truncate max-w-[180px]">{r[f.label] || <span className="text-[#9CA3AF]">—</span>}</td>)}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                {results[kind] && (
                  <div className="mt-4 p-3 rounded-lg bg-[#DCFCE7] border border-[#2F8F6E]/20 text-[12px] text-[#2F8F6E] flex items-center gap-2">
                    <CheckCircle size={14} weight="fill" /> Imported {results[kind].imported} · skipped {results[kind].skipped_duplicate} · invalid {results[kind].skipped_invalid}
                  </div>
                )}
              </div>
            );
          })()}

          {lastStep && (
            <div className="p-10 text-center">
              <div className="w-14 h-14 rounded-full bg-[#DCFCE7] mx-auto flex items-center justify-center mb-3">
                <CheckCircle size={28} weight="fill" className="text-[#2F8F6E]" />
              </div>
              <div className="font-display font-semibold text-[22px] tracking-tight text-[#121216]">Migration complete</div>
              <div className="text-[13px] text-[#4B5563] mt-2 max-w-md mx-auto leading-relaxed">Here's what landed in your POS:</div>
              <div className="mt-6 grid grid-cols-3 gap-3 max-w-2xl mx-auto">
                {orderedKinds.map((k) => (
                  <div key={k} className="p-4 rounded-2xl border border-[#E5E7EB] bg-white">
                    <div className="text-[10px] uppercase tracking-[0.14em] text-[#9CA3AF] font-semibold">{PRESETS[k].label}</div>
                    <div className="font-display font-semibold text-[28px] mt-1 text-[#121216] tabular-nums">{(results[k]?.imported ?? 0)}</div>
                    <div className="text-[10px] text-[#9CA3AF] mt-1">
                      {results[k] ? `${results[k].skipped_duplicate} dup · ${results[k].skipped_invalid} invalid` : "skipped"}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Progress bar for chunked imports */}
        {submitting && progress && progress.total > 0 && (
          <div className="px-8 pb-3 -mt-2" data-testid="mig-progress">
            <div className="flex items-center justify-between text-[11px] font-mono text-slate-600 mb-1.5">
              <span>
                <span className="font-bold text-[#1E5FEB]">
                  {progress.done.toLocaleString()}
                </span>
                <span className="text-slate-400"> / {progress.total.toLocaleString()} rows</span>
                {progress.chunks > 0 && (
                  <span className="text-slate-400 ml-2">
                    · chunk {progress.chunkIndex || 0} / {progress.chunks}
                  </span>
                )}
              </span>
              <span className="font-bold text-[#1E5FEB]">
                {Math.round(((progress.done || 0) / progress.total) * 100)}%
              </span>
            </div>
            <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-[#1E5FEB] to-[#3B82F6] transition-all duration-300"
                style={{ width: `${Math.round(((progress.done || 0) / progress.total) * 100)}%` }}
              />
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="px-8 py-4 border-t border-[#F3F4F6] flex items-center justify-between bg-white">
          <button onClick={() => { if (step === 0) onClose(); else if (lastStep) { reset(); onCompleted?.(); onClose(); } else setStep(Math.max(0, step - 1)); }}
            className="h-10 px-4 rounded-full text-[12px] font-semibold text-[#4B5563] hover:text-[#121216] inline-flex items-center gap-1.5">
            {lastStep ? "Close" : step === 0 ? "Cancel" : <><ArrowLeft size={12} weight="bold" /> Back</>}
          </button>
          <div className="flex items-center gap-2">
            {step >= 1 && step <= 3 && !sheets[orderedKinds[step - 1]] && (
              <button onClick={skipThis} className="h-10 px-4 rounded-full text-[12px] font-semibold text-[#4B5563] hover:text-[#121216]">Skip</button>
            )}
            {step >= 1 && step <= 3 && sheets[orderedKinds[step - 1]] && !results[orderedKinds[step - 1]] && (
              <button
                onClick={advance}
                disabled={submitting}
                data-testid={`mig-import-${orderedKinds[step - 1]}`}
                className="h-10 px-5 rounded-full bg-[#1E5FEB] hover:bg-[#1849C8] disabled:bg-[#9CA3AF] text-white text-[12px] font-semibold inline-flex items-center gap-1.5"
              >
                {submitting
                  ? (progress?.total
                      ? `Importing… ${progress.done?.toLocaleString() || 0} / ${progress.total.toLocaleString()}`
                      : "Importing…")
                  : <>Import {sheets[orderedKinds[step - 1]].rows.length} rows <ArrowRight size={12} weight="bold" /></>}
              </button>
            )}
            {step >= 1 && step <= 3 && results[orderedKinds[step - 1]] && (
              <button onClick={() => setStep(step + 1)} className="h-10 px-5 rounded-full bg-[#121216] hover:bg-[#1E5FEB] text-white text-[12px] font-semibold inline-flex items-center gap-1.5">
                Next <ArrowRight size={12} weight="bold" />
              </button>
            )}
            {lastStep && (
              <button onClick={() => { reset(); onCompleted?.(); onClose(); }} className="h-10 px-5 rounded-full bg-[#1E5FEB] hover:bg-[#1849C8] text-white text-[12px] font-semibold">Done</button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function StepDot({ active, done, label }) {
  return (
    <div className={`flex items-center gap-1 ${done ? "text-[#2F8F6E]" : active ? "text-[#121216]" : "text-[#9CA3AF]"}`}>
      <span className={`w-4 h-4 rounded-full flex items-center justify-center text-[8px] font-bold ${done ? "bg-[#2F8F6E] text-white" : active ? "bg-[#121216] text-white" : "bg-[#E5E7EB]"}`}>
        {done ? "✓" : ""}
      </span>
      <span>{label}</span>
    </div>
  );
}
